import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Link2, Search, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import { EmptyState, Spinner, StatusBadge, fmtDay, gbp2, inputCls } from "@/components/portal/shared";

const QUOTE_STATUSES = ["Quote Created", "Quote Sent", "Changes Requested", "Accepted", "Declined"];
const QUOTE_BADGE = { Accepted: "Quote Accepted", Declined: "Lost", "Changes Requested": "Follow-Up" };

export default function QuotesPage() {
  const { api } = usePortalAuth();
  const [quotes, setQuotes] = useState(null);
  const [leadMap, setLeadMap] = useState({});
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () =>
    Promise.all([api.get("/quotes"), api.get("/leads")]).then(([qu, le]) => {
      setQuotes(qu.data.quotes);
      setLeadMap(Object.fromEntries(le.data.leads.map((l) => [l.id, l])));
    }).catch(() => setQuotes([]));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    let r = quotes || [];
    if (status) r = r.filter((x) => x.status === status);
    if (q) {
      const needle = q.toLowerCase();
      r = r.filter((x) => {
        const c = leadMap[x.lead_id];
        return `${x.ref} ${c?.firstName || ""} ${c?.lastName || ""}`.toLowerCase().includes(needle);
      });
    }
    return r;
  }, [quotes, q, status, leadMap]);

  if (quotes === null) return <Spinner />;

  const send = async (quote) => {
    setBusyId(quote.id);
    try {
      await api.post(`/quotes/${quote.id}/send`);
      toast.success(`Quote ${quote.ref} emailed to customer`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const copyLink = (quote) => {
    const url = `${window.location.origin}/quote/${quote.ref}/${quote.token}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Customer quote link copied"));
  };

  const remove = async (x) => {
    if (!window.confirm(`Delete quotation ${x.ref}? This cannot be undone.`)) return;
    try {
      await api.delete(`/quotes/${x.id}`);
      toast.success(`Quotation ${x.ref} deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="quotes-page" className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="quotes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref or customer…" className={`${inputCls} pl-10 rounded-full`} />
        </div>
        <select data-testid="quotes-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-auto`} aria-label="Filter by status">
          <option value="">All statuses</option>
          {QUOTE_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={FileText}>No quotes yet. Build one from a customer record or the dashboard quick actions.</EmptyState>
      ) : (
        <div className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-warm-200 bg-warm-100/60">
                <th className="px-5 py-3">Ref</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-200">
              {rows.map((x) => {
                const c = leadMap[x.lead_id] || {};
                return (
                  <tr key={x.id} className="hover:bg-warm-100/50 transition-colors" data-testid={`quote-row-${x.id}`}>
                    <td className="px-5 py-3.5 font-semibold text-navy-900">{x.ref}</td>
                    <td className="px-4 py-3.5">
                      <Link to={`/portal/customers/${x.lead_id}?tab=quotes`} className="font-medium text-slate-800 hover:underline">{c.firstName} {c.lastName}</Link>
                    </td>
                    <td className="px-4 py-3.5 font-semibold">{gbp2(x.total)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-400">{fmtDay(x.created_at)}</td>
                    <td className="px-4 py-3.5"><StatusBadge s={QUOTE_BADGE[x.status] || x.status} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-2">
                        <button type="button" data-testid={`quote-copy-${x.id}`} onClick={() => copyLink(x)} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
                          <Link2 className="h-3 w-3" /> Link
                        </button>
                        {x.status === "Quote Created" && c.email && (
                          <button type="button" data-testid={`quote-send-${x.id}`} disabled={busyId === x.id} onClick={() => send(x)} className="inline-flex items-center gap-1 rounded-full bg-navy-900 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-navy-800 disabled:opacity-40">
                            <Send className="h-3 w-3" /> Email
                          </button>
                        )}
                        {x.status !== "Accepted" && (
                          <button type="button" data-testid={`quote-delete-${x.id}`} onClick={() => remove(x)} aria-label={`Delete ${x.ref}`} title="Delete quotation" className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-red-600 hover:border-red-300 hover:bg-red-50">
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
